import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiPlay, FiPlus, FiCheck, FiStar, FiYoutube } from 'react-icons/fi';
import { IMG_BASE } from '../api/tmdb';
import { useMovieContext } from '../context/MovieContext';
import TrailerModal from './TrailerModal';

const MovieCard = ({ movie, size = 'md' }) => {
  const navigate = useNavigate();
  const { addToWatchlist, removeFromWatchlist, isInWatchlist } = useMovieContext();
  const [hovered,  setHovered]  = useState(false);
  const [rotateX,  setRotateX]  = useState(0);
  const [rotateY,  setRotateY]  = useState(0);
  const [showYT,   setShowYT]   = useState(false);
  const cardRef    = useRef();
  const inWatchlist = isInWatchlist(movie.id);
  const isCustom   = movie.source === 'custom';

  const sizes = { sm: { w: 130, h: 195 }, md: { w: 160, h: 240 }, lg: { w: 200, h: 300 } };
  const { w, h } = sizes[size] || sizes.md;

  const onMouseMove = (e) => {
    if (!cardRef.current) return;
    const r  = cardRef.current.getBoundingClientRect();
    const dx = (e.clientX - r.left - r.width  / 2) / (r.width  / 2);
    const dy = (e.clientY - r.top  - r.height / 2) / (r.height / 2);
    setRotateY(dx * 12); setRotateX(-dy * 12);
  };

  const onLeave = () => { setHovered(false); setRotateX(0); setRotateY(0); };

  // Resolve poster image — TMDB path or custom URL
  const poster = isCustom
    ? (movie.posterUrl || null)
    : (movie.poster_path ? IMG_BASE + '/w342' + movie.poster_path : null);

  const handlePlay = (e) => {
    e.stopPropagation();
    if (isCustom && movie.youtubeId) { setShowYT(true); return; }
    navigate('/movie/' + movie.id);
  };

  const handleCard = () => {
    if (isCustom) { if (movie.youtubeId) setShowYT(true); return; }
    navigate('/movie/' + movie.id);
  };

  return (
    <>
      <motion.div
        ref={cardRef}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={onLeave}
        onMouseMove={onMouseMove}
        onClick={handleCard}
        animate={{ rotateX: hovered ? rotateX : 0, rotateY: hovered ? rotateY : 0, scale: hovered ? 1.08 : 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        style={{ width: w, minWidth: w, perspective: 1000, transformStyle: 'preserve-3d', cursor: 'pointer' }}
        className="relative flex-shrink-0"
      >
        <div className="relative rounded-xl overflow-hidden"
          style={{
            height: h,
            background: 'var(--card)',
            boxShadow: hovered
              ? '0 25px 60px rgba(0,0,0,0.7), 0 0 30px rgba(229,9,20,0.25)'
              : '0 8px 20px rgba(0,0,0,0.4)',
            border: hovered ? '1px solid rgba(229,9,20,0.3)' : '1px solid rgba(255,255,255,0.04)',
            transition: 'box-shadow 0.3s, border 0.3s',
          }}
        >
          {/* Poster */}
          {poster
            ? <img src={poster} alt={movie.title} className="w-full h-full object-cover"
                style={{ filter: hovered ? 'brightness(0.65)' : 'brightness(1)', transition: 'filter 0.3s' }} />
            : <div className="w-full h-full flex items-center justify-center" style={{ background: '#1a1a2e' }}>
                <FiPlay size={32} style={{ color: 'var(--muted)' }} />
              </div>
          }

          {/* Custom badge */}
          {isCustom && (
            <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full flex items-center gap-1"
              style={{ background: 'rgba(245,197,24,0.9)' }}>
              <FiYoutube size={9} color="#000" />
              <span style={{ color: '#000', fontSize: 9, fontWeight: 700, fontFamily: 'monospace' }}>ORIGINAL</span>
            </div>
          )}

          {/* Glow */}
          {hovered && <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(135deg,rgba(229,9,20,0.12) 0%,transparent 60%)' }} />}

          {/* Hover overlay */}
          <motion.div
            animate={{ opacity: hovered ? 1 : 0, y: hovered ? 0 : 16 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 flex flex-col justify-end p-3"
            style={{ background: 'linear-gradient(to top,rgba(10,10,15,0.97) 0%,rgba(10,10,15,0.4) 60%,transparent 100%)' }}
          >
            <p className="font-display text-sm leading-tight mb-1 line-clamp-2" style={{ color: 'var(--text)' }}>
              {movie.title}
            </p>
            <div className="flex items-center gap-2 mb-3">
              <FiStar size={11} fill="var(--gold)" color="var(--gold)" />
              <span className="text-xs font-accent" style={{ color: 'var(--gold)' }}>
                {(movie.vote_average || movie.rating || 0).toFixed(1)}
              </span>
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                {(movie.release_date || movie.releaseYear || '').toString().split('-')[0]}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                onClick={handlePlay}
                className="flex-1 py-2 rounded-lg text-xs font-body font-semibold flex items-center justify-center gap-1"
                style={{ background: 'var(--red)', color: '#fff' }}>
                {isCustom ? <><FiYoutube size={11} /> Play</> : <><FiPlay size={11} fill="currentColor" /> Play</>}
              </motion.button>
              {!isCustom && (
                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  onClick={(e) => { e.stopPropagation(); inWatchlist ? removeFromWatchlist(movie.id) : addToWatchlist(movie); }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center glass"
                  style={{ color: inWatchlist ? 'var(--gold)' : 'var(--text)' }}>
                  {inWatchlist ? <FiCheck size={13} /> : <FiPlus size={13} />}
                </motion.button>
              )}
            </div>
          </motion.div>

          {/* Rating badge (idle) */}
          {!hovered && (
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full flex items-center gap-1"
              style={{ background: 'rgba(10,10,15,0.82)', backdropFilter: 'blur(8px)' }}>
              <FiStar size={9} fill="var(--gold)" color="var(--gold)" />
              <span className="text-xs font-accent" style={{ color: 'var(--gold)' }}>
                {(movie.vote_average || movie.rating || 0).toFixed(1)}
              </span>
            </div>
          )}

          {/* 3-D shine */}
          {hovered && (
            <div className="absolute inset-0 pointer-events-none" style={{
              background: `radial-gradient(circle at ${50 + rotateY * 2}% ${50 - rotateX * 2}%,rgba(255,255,255,0.07) 0%,transparent 60%)`,
            }} />
          )}
        </div>
      </motion.div>

      {/* YouTube embed for custom movies */}
      {isCustom && movie.youtubeId && (
        <TrailerModal videoKey={showYT ? movie.youtubeId : null} onClose={() => setShowYT(false)} />
      )}
    </>
  );
};

export default MovieCard;
